import { cn } from '@/lib/utils';
import { CircleAlert } from 'lucide-react';
import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

type InputRootProps = React.HtmlHTMLAttributes<HTMLDivElement>;

function InputWrapper({ className, ...props }: InputRootProps) {
	return (
		<div
			className={cn(
				'focus-visible:ring-ring flex h-10 w-full items-center gap-2 rounded-md border border-zinc-200 bg-inherit px-3 py-2 text-sm ring-offset-0 transition-all duration-150 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-zinc-500 focus-within:ring-1 focus-within:ring-cyan-800 focus-within:ring-offset-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
				className,
			)}
			{...props}
		/>
	);
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, type, ...props }, ref) => {
		const [showPassword, setShowPassword] = useState(false);

		if (type === 'password') {
			return (
				<div className="relative">
					<input
						type={showPassword ? 'text' : 'password'}
						className={cn(
							'w-full flex-1 border-0 bg-transparent p-0 pr-8 text-dark outline-none placeholder:text-sm placeholder:text-zinc-500 focus:outline-none',
							className,
						)}
						ref={ref}
						{...props}
					/>
					<button
						type="button"
						className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors hover:text-zinc-600 focus:outline-none"
						onClick={() => setShowPassword(!showPassword)}
						tabIndex={-1}
					>
						{showPassword ? (
							<EyeOff className="h-4 w-4" />
						) : (
							<Eye className="h-4 w-4" />
						)}
					</button>
				</div>
			);
		}

		return (
			<input
				type={type}
				className={cn(
					'w-full flex-1 border-0 bg-transparent p-0 text-dark outline-none placeholder:text-sm placeholder:text-zinc-500 focus:outline-none',
					type === 'date' &&
						`
            [&::-webkit-datetime-edit-day-field]:text-zinc-500
            [&::-webkit-datetime-edit-fields-wrapper]:text-zinc-500
            [&::-webkit-datetime-edit-month-field]:text-zinc-500
            [&::-webkit-datetime-edit-text]:text-zinc-500
            [&::-webkit-datetime-edit-year-field]:text-zinc-500
            [&::-webkit-datetime-edit]:text-sm
            [&::-webkit-datetime-edit]:text-zinc-500 
          `,
					className,
				)}
				ref={ref}
				{...props}
			/>
		);
	},
);
Input.displayName = 'Input';

type InputLabelProps = {
	text?: string;
	className?: string;
	icon?: React.ReactNode;
} & React.LabelHTMLAttributes<HTMLLabelElement>;

const InputLabel = ({ text, className, icon, ...props }: InputLabelProps) => {
	return (
		<div className="flex items-center gap-1">
			{icon && icon}
			<label
				className={cn('text-sm font-medium text-dark', className)}
				{...props}
			>
				{text}
			</label>
		</div>
	);
};

type InputErrorProps = {
	error: string;
	className?: string;
};

const InputError = ({ error, className }: InputErrorProps) => {
	return (
		<div className="flex items-center gap-1 pt-2">
			<CircleAlert className="h-3 w-3 text-red-500" />
			<p className={cn('text-xs  font-semibold text-red-500', className)}>
				{error}
			</p>
		</div>
	);
};

export { Input, InputWrapper, InputLabel, InputError };
